import { notification } from 'antd'
import i18next from 'i18next'
import { action, computed, makeAutoObservable, observable } from 'mobx'

import { FileEntityType, fileService } from 'services/FileService'
import { IRootStore } from 'store/types'
import { ConvertedFile } from 'utils/helpers/files/fileUpload'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { WEB_LINK_REGEXP } from 'shared/constants/regExp'
import { PHOTO_FORMATS, VIDEO_FORMATS } from 'shared/constants/values'

import { OrderStatus } from '../../OrderModule/types'
import { ticketApi } from '../api'
import { TicketFileUploadDTO, TicketPhotoType, TicketReason, TicketType } from '../types'
import { initialTicketFileValue, TicketKeys, ticketReasonLabels } from './../data'

const getKey = (key: string) => `${TicketKeys.ticketModule}.${key}`

export class CreateTicketUiStore {
  @observable ticketTypeValue = TicketType.openingOrder
  @observable ticketReasonValue = TicketReason.eLock
  @observable selectedOrderId: null | number = null
  @observable description = ''
  @observable link = ''

  @observable files: ConvertedFile[] = []
  /** Фасад здания днем */
  @observable generalPhoto: ConvertedFile = initialTicketFileValue
  /** Документ БТИ */
  @observable btiDocument: ConvertedFile = initialTicketFileValue

  constructor(readonly rootStore: IRootStore) {
    makeAutoObservable(this)
  }

  @computed
  get ordersOptions() {
    return this.rootStore.orderStore.list
      .filter((order) => order.status === OrderStatus.accepted)
      .map((order) => ({ value: order.id, label: order.fullAddress }))
  }

  @computed
  get getFiles() {
    return this.ticketTypeValue === TicketType.openingOrder ? this.files : [this.generalPhoto, this.btiDocument]
  }

  @computed
  get invalidPhotos() {
    return this.getFiles.some((file) => !file || !file.content || file.content.length === 0)
  }

  @computed
  get invalidVideoUrl() {
    return this.link.trim() && !WEB_LINK_REGEXP.test(this.link)
  }

  @action
  setTicketTypeValue(value: TicketType) {
    this.ticketTypeValue = value
  }

  @action
  setTicketReasonValue(value: TicketReason) {
    this.ticketReasonValue = value
  }

  @action
  setSelectedOrderId(value: number) {
    this.selectedOrderId = value
  }

  @action
  setDescription(value: string) {
    this.description = value
  }

  @action
  setLink(value: string) {
    this.link = value
  }

  @action
  setFiles(value: ConvertedFile[]) {
    this.files = value
  }

  @action
  setFileData = (key: TicketPhotoType) =>
    action((item: ConvertedFile) => {
      if (this[key]) {
        this[key].content = item.content
        this[key].fileSize = item.fileSize
        this[key].format = item.format
        this[key].name = item.name
      }
    })

  reset = () => {
    this.setDescription('')
    this.setFiles([])
    this.setLink('')
    this.setFileData(TicketPhotoType.generalPhoto)(initialTicketFileValue)
    this.setFileData(TicketPhotoType.btiDocument)(initialTicketFileValue)
  }

  getCreateTicketArgs(orderId: number, photos: (TicketFileUploadDTO | null)[], videos: (TicketFileUploadDTO | null)[]) {
    const reason =
      this.ticketTypeValue === TicketType.openingOrder
        ? `${i18next.t(getKey('createTicket.Причина обращения'))}: ${ticketReasonLabels[this.ticketReasonValue]}\n`
        : ''
    const descriptionProblem =
      this.ticketTypeValue === TicketType.openingOrder
        ? `${i18next.t(getKey('createTicket.Описание проблемы'))}: ${this.description.trim()}\n`
        : ''

    return {
      ticketType: this.ticketTypeValue,
      ticketReason: this.ticketTypeValue === TicketType.openingOrder ? this.ticketReasonValue : undefined,
      description: [
        reason,
        descriptionProblem,
        this.link ? `${i18next.t(getKey('createTicket.Прикрепленная ссылка'))}: ${this.link.trim()}` : '',
      ].join(''),
      files: {
        image_urls: photos.map((file) => file?.url ?? ''),
        video_urls: videos.map((file) => file?.url ?? ''),
        document_urls: [],
      },
      orderId,
    }
  }

  async createTicket() {
    if (!this.selectedOrderId) {
      return false
    }

    const photos = await this.fileUpload(
      this.getFiles.filter((file) => PHOTO_FORMATS.includes(file.format.toLowerCase())),
    )
    const videos = await this.fileUpload(
      this.getFiles.filter((file) => VIDEO_FORMATS.includes(file.format.toLowerCase())),
    )

    const files = [...photos, ...videos]

    if (files.length < this.getFiles.length || files.some((file) => file?.url === '')) {
      notification.error({
        message: i18next.t(getSharedKey('api.filesUploadError')),
        description: i18next.t(getKey('api.ticketUploadFilesErrorMessage')),
      })

      return false
    }

    try {
      const result = await ticketApi.createTicket(this.getCreateTicketArgs(this.selectedOrderId, photos, videos))

      notification.success({
        message: i18next.t(getKey('api.createTicketSuccess'), {
          count: result.ticketId,
        }),
      })

      return true
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('api.createTicketErrorMessage')),
        description: (error as Error).message,
      })
      return false
    }
  }

  async fileUpload(files: ConvertedFile[]) {
    try {
      const promiseFiles: Promise<TicketFileUploadDTO | null>[] = []

      for (const file of files) {
        /* mytodo: поменять после выкатки замков */
        const res = fileService.ticketFileUpload({
          totalSize: file.fileSize,
          content: file.content,
          entityType: FileEntityType.ticket,
        })

        promiseFiles.push(res)
      }

      return await Promise.all(promiseFiles)
    } catch (error) {
      notification.error({
        message: i18next.t(getSharedKey('api.filesUploadError')),
        description: (error as Error).message,
      })

      return []
    }
  }
}
